import React from 'react';
import { Autocomplete, TextField, FormControl, FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  marginTop: '12px',
  width: '100%',

  '& .MuiInputLabel-root': {
    fontWeight: 'bold',
    backgroundColor: 'transparent',
    color: `${theme.palette.primary.main} !important`,
    '&.MuiInputLabel-shrink': {
      backgroundColor: theme.palette.background.paper || 'white',
      padding: '0 4px',
      transform: 'translate(14px, -9px) scale(0.75)',
      color: `${theme.palette.primary.main} !important`,
    },
    '&.Mui-focused': {
      color: `${theme.palette.primary.main} !important`,
    },
    '&.MuiFormLabel-filled': {
      color: `${theme.palette.primary.main} !important`,
    },
  },

  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
      opacity: 0.8,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 3,
      opacity: 0.6,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 3,
    },
  },

  '@media (max-width: 600px)': {
    marginTop: '8px',
    '& .MuiInputLabel-root': {
      fontSize: '14px',
    },
  },
}));

const sizeConfigMap = {
  small: { height: 40, fontSize: 14, labelSize: 12 },
  medium: { height: 56, fontSize: 18, labelSize: 18 },
  large: { height: 64, fontSize: 18, labelSize: 16 },
  xlarge: { height: 72, fontSize: 20, labelSize: 18 },
};

const normalizeOption = (option = {}, valueKey = 'value', labelKey = 'label') => {
  if (typeof option === 'string' || typeof option === 'number') {
    return { [valueKey]: option, [labelKey]: String(option) };
  }
  if (option && typeof option === 'object') {
    const value = option[valueKey];
    const label =
      option[labelKey] ||
      option.name ||
      option.title ||
      option.text ||
      (typeof value !== 'undefined' ? String(value) : '');
    return { ...option, [valueKey]: value, [labelKey]: label };
  }
  return { [valueKey]: '', [labelKey]: '' };
};

const CustomAutoComplete = ({
  label,
  name,
  value,
  onChange,
  options,
  getOptionLabel,
  isOptionEqualToValue,
  optionValueKey = 'value',
  optionLabelKey = 'label',
  placeholder = '',
  inputLabel,
  inputPlaceholder,
  sx = {},
  size = 'medium',
  error = false,
  helperText,
  FormHelperTextProps = {},
  disabled = false,
  loading = false,
  multiple = false,
  renderInput,
  onInputChange,
  inputValue,
  children,
  ...props
}) => {
  const sizeConfig = sizeConfigMap[size] || sizeConfigMap.medium;
  const arrayOptions = React.useMemo(
    () => (Array.isArray(options) ? options : []),
    [options]
  );

  const childrenOptions = React.useMemo(
    () =>
      (React.Children.toArray(children) || [])
        .map((child) => {
          if (!React.isValidElement(child)) return null;
          const childValue = child.props?.value;
          const childLabel = child.props?.children || child.props?.label;
          return normalizeOption(
            { [optionValueKey]: childValue, [optionLabelKey]: childLabel },
            optionValueKey,
            optionLabelKey
          );
        })
        .filter(Boolean),
    [children, optionLabelKey, optionValueKey]
  );

  const normalizedOptions = React.useMemo(
    () =>
      (arrayOptions.length ? arrayOptions : childrenOptions).map((opt) =>
        normalizeOption(opt, optionValueKey, optionLabelKey)
      ),
    [arrayOptions, childrenOptions, optionLabelKey, optionValueKey]
  );

  const selectedOption = multiple
    ? (Array.isArray(value)
        ? value.map((val) =>
            typeof val === 'object'
              ? normalizeOption(val, optionValueKey, optionLabelKey)
              : normalizeOption({ [optionValueKey]: val }, optionValueKey, optionLabelKey)
          )
        : [])
    : normalizedOptions.find((opt) => opt[optionValueKey] === value) || null;

  const baseStyles = {
    '& .MuiInputLabel-root': {
      fontSize: sizeConfig.labelSize,
    },
    '& .MuiOutlinedInput-root': {
      height: multiple ? 'auto' : sizeConfig.height,
      minHeight: sizeConfig.height,
      fontSize: sizeConfig.fontSize,
      alignItems: multiple ? 'flex-start' : 'center',
      paddingTop: multiple ? 0.5 : undefined,
      paddingBottom: multiple ? 0.5 : undefined,
    },
    '& .MuiInputBase-input': {
      padding: '0 14px',
      fontSize: sizeConfig.fontSize,
    },
    ...(multiple && {
      '& .MuiAutocomplete-inputRoot': {
        flexWrap: 'wrap',
        alignItems: 'center',
        paddingTop: 6,
        paddingBottom: 4,
      },
      '& .MuiChip-root': {
        margin: '4px 4px 0 0',
      },
      '& .MuiAutocomplete-endAdornment': {
        top: 8,
      },
    }),
    ...sx,
  };

  const handleChange = (_, newValue) => {
    const finalValue = multiple
      ? (Array.isArray(newValue)
          ? newValue.map((val) =>
              val && typeof val === 'object' ? val[optionValueKey] : val || ''
            )
          : [])
      : newValue && typeof newValue === 'object'
        ? newValue[optionValueKey]
        : newValue || '';

    if (onChange) {
      // Mantiene compatibilidad con usos existentes (evento sintético similar a TextField)
      onChange({ target: { name, value: finalValue } }, newValue);
    }
  };

  return (
    <StyledFormControl sx={baseStyles} error={error}>
      <Autocomplete
        options={normalizedOptions}
        value={selectedOption}
        loading={loading}
        disabled={disabled}
        multiple={multiple}
        onChange={handleChange}
        onInputChange={onInputChange}
        inputValue={inputValue}
        isOptionEqualToValue={
          isOptionEqualToValue ||
          ((option, val) =>
            (option && option[optionValueKey]) ===
            (val && (typeof val === 'object' ? val[optionValueKey] : val)))
        }
        getOptionLabel={
          getOptionLabel ||
          ((option) => {
            if (typeof option === 'string') return option;
            return option?.[optionLabelKey] ?? '';
          })
        }
        renderInput={
          renderInput ||
          ((params) => (
            <TextField
              {...params}
              label={inputLabel ?? label}
              placeholder={inputPlaceholder ?? placeholder}
              error={error}
              helperText={helperText}
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
            />
          ))
        }
        {...props}
      />
      {helperText === '' && !error && (
        <FormHelperText {...FormHelperTextProps}>&nbsp;</FormHelperText>
      )}
    </StyledFormControl>
  );
};

export default CustomAutoComplete;
